const idrFormatter = new Intl.NumberFormat("id-ID");

export function formatPrice(value: number) {
  return `Rp${idrFormatter.format(value)}`;
}

export function formatCountdown(totalSeconds: number) {
  const safe = Math.max(totalSeconds, 0);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Copy for the bank-transfer rail.
 *
 * Nested rather than flattened into QrisLabels because the two rails have a
 * title and an instruction each. Flat keys meant the QRIS title ("Scan to pay
 * with QRIS") rendered above the bank selector.
 */
export interface VaLabels {
  title: string;
  instruction: string;
  bankLabel: string;
  accountLabel: string;
  copy: string;
  copied: string;
  expiryHint: string;
  error: string;
  railBank: string;
  railQris: string;
  railHosted: string;
}

export interface HostedPaymentLabels {
  title: string;
  instruction: string;
  cardTitle: string;
  cardMethods: string;
  walletTitle: string;
  walletMethods: string;
  continue: string;
  feeNote: string;
}

export interface QrisLabels {
  title: string;
  /** Heading for the whole payment step, above the rail tabs. */
  checkoutTitle?: string;
  instruction: string;
  amountLabel: string;
  statusPending: string;
  statusSuccess: string;
  statusFailed: string;
  saveImage: string;
  saveHint: string;
  payInWallet?: string;
  walletOnlyTitle?: string;
  walletOnlyInstruction?: string;
  qrCodeLabel?: string;
  expiresIn: string;
  expired: string;
  retry: string;
  secure: string;
  /** Grace-window copy after the countdown hits zero while polling continues.
   * Optional until dictionaries ship it; Indonesian default in the step. */
  stillChecking?: string;
  /** One-tap reissue CTA on genuine expiry. Optional until dictionaries ship
   * it; Indonesian default in the step. */
  newQr?: string;
  /** Bank-transfer rail copy. Optional until every dictionary ships it. */
  va?: VaLabels;
  /** Singapay-hosted card and e-wallet checkout copy. */
  hosted?: HostedPaymentLabels;
}

export const DEFAULT_VA_LABELS: VaLabels = {
  title: "Transfer bank",
  instruction:
    "Pilih bank kamu, lalu transfer ke nomor virtual account di bawah lewat aplikasi bank atau ATM.",
  bankLabel: "Pilih bank",
  accountLabel: "Nomor Virtual Account",
  copy: "Salin nomor",
  copied: "Tersalin",
  expiryHint: "Nomor ini aktif 24 jam, jadi kamu bisa transfer nanti.",
  error:
    "Nomor virtual account belum bisa dibuat. Coba lagi atau bayar pakai QRIS.",
  railBank: "Transfer Bank",
  railQris: "QRIS",
  railHosted: "Kartu & e-wallet",
};

export const DEFAULT_HOSTED_PAYMENT_LABELS: HostedPaymentLabels = {
  title: "Bayar dengan kartu atau e-wallet",
  instruction:
    "Lanjutkan ke halaman aman Singapay, lalu pilih metode pembayaranmu.",
  cardTitle: "Kartu kredit",
  cardMethods: "Visa, Mastercard, American Express",
  walletTitle: "E-wallet",
  walletMethods: "DANA, OVO, ShopeePay",
  continue: "Lanjut ke Singapay",
  feeNote: "Tidak ada biaya tambahan untuk kamu.",
};

export const DEFAULT_STILL_CHECKING_LABEL =
  "Sudah bayar? Kami masih mengecek status pembayaranmu.";

export const DEFAULT_NEW_QR_LABEL = "Buat kode QR baru";

export async function saveQrCanvasImage(
  canvas: HTMLCanvasElement | null,
  paymentId: string,
): Promise<void> {
  if (!canvas) {
    return;
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/png");
  });

  if (blob && typeof navigator.share === "function") {
    const file = new File([blob], `qris-${paymentId}.png`, {
      type: "image/png",
    });
    const shareData = { files: [file], title: `QRIS ${paymentId}` };

    const canShare =
      "canShare" in navigator &&
      typeof navigator.canShare === "function" &&
      navigator.canShare(shareData);

    if (canShare) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
      }
    }
  }

  const url = canvas.toDataURL("image/png");
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Requests a fresh QR for the same payment intent. Throws when the server
 * rejects the reissue so the caller can surface an error state.
 */
export async function requestQrisReissue(
  paymentId: string,
  claimToken?: string,
): Promise<{ qrString: string | null; expiresAt: string | null }> {
  const reissueUrl = new URL(
    `/api/payment/${paymentId}/reissue`,
    window.location.origin,
  );
  if (claimToken) {
    reissueUrl.searchParams.set("claimToken", claimToken);
  }

  const response = await fetch(reissueUrl.toString(), { method: "POST" });
  if (!response.ok) {
    throw new Error("Failed to reissue QR");
  }

  const body = (await response.json()) as {
    qrString?: string | null;
    expiresAt?: string | null;
  };

  return {
    qrString: body.qrString ?? null,
    expiresAt: body.expiresAt ?? null,
  };
}
